import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {ScanLine} from 'lucide-react';
import {beep, inputClass, normalizeTag} from './lib';

type Props = {
    placeholder: string;
    // Returns an error message to flash, or null on success.
    onScan: (tag: string) => Promise<string | null>;
    disabled?: boolean;
    autoFocus?: boolean;
};

// One input that a keyboard-wedge scan gun (tag + Enter/Tab) or a human can drive.
// Re-focuses itself after every scan so the gun never needs the mouse.
const ScanInput: React.FC<Props> = ({placeholder, onScan, disabled, autoFocus = true}) => {
    const ref = useRef<HTMLInputElement>(null);
    const [value, setValue] = useState('');
    const [busy, setBusy] = useState(false);
    const [flash, setFlash] = useState<{ ok: boolean; message: string } | null>(null);

    useEffect(() => {
        if (!flash) return;
        const t = setTimeout(() => setFlash(null), flash.ok ? 1200 : 3500);
        return () => clearTimeout(t);
    }, [flash]);

    // Refocus after a scan finishes (focusing while disabled is a no-op). Only on the
    // busy→idle transition, so the initial render respects `autoFocus={false}`.
    const wasBusy = useRef(false);
    useEffect(() => {
        if (wasBusy.current && !busy && !disabled) {
            ref.current?.focus();
            ref.current?.select();
        }
        wasBusy.current = busy;
    }, [busy, disabled]);

    const submit = async () => {
        const tag = normalizeTag(value);
        if (!tag || busy) return;
        setBusy(true);
        try {
            const error = await onScan(tag);
            beep(!error);
            setFlash(error ? {ok: false, message: error} : {ok: true, message: tag});
            if (!error) setValue('');
        } finally {
            setBusy(false);
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            void submit();
        }
    };

    return (
        <div>
            <label className="relative block">
                <ScanLine className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
                <input
                    ref={ref}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    disabled={disabled || busy}
                    autoFocus={autoFocus}
                    autoComplete="off"
                    spellCheck={false}
                    className={`${inputClass} w-full pl-9 font-mono transition-colors ${
                        flash ? (flash.ok ? 'border-emerald-500' : 'border-red-500 bg-red-950/40') : ''
                    }`}
                />
            </label>
            {flash && (
                <p className={`mt-1 text-xs ${flash.ok ? 'text-emerald-400' : 'text-red-400 font-bold'}`} role="status">
                    {flash.ok ? `✓ ${flash.message}` : flash.message}
                </p>
            )}
        </div>
    );
};

export default ScanInput;
